/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { ROLE_WEIGHTS, SUPER_ADMIN_ROLE } from 'modules/common/constants/roles';

/**
 * Checks whether a user holds the given role. Users can have multiple roles,
 * so prefer this over reading `roles[0]`.
 * @param {Object} user - User object with a `roles` array of `{ name }`.
 * @param {string} roleName - Role key, e.g. SUPER_ADMIN_ROLE.
 * @returns {boolean}
 */
export const hasRole = (user, roleName) =>
  user?.roles?.some((userRole) => userRole?.name === roleName) ?? false;

/**
 * Rank of a single role from ROLE_WEIGHTS. Unknown roles rank lowest.
 * @param {string} roleName
 * @returns {number}
 */
export const getRoleWeight = (roleName) => ROLE_WEIGHTS[roleName] ?? 0;

/**
 * Rank of a user, i.e. the highest-ranked role they hold. -1 when they have no roles.
 * @param {Object} user - User object with a `roles` array of `{ name }`.
 * @returns {number}
 */
export const getUserWeight = (user) =>
  Math.max(-1, ...(user?.roles ?? []).map((userRole) => getRoleWeight(userRole?.name)));

/**
 * Whether `user` ranks strictly above `otherUser` (peers do not outrank each other).
 * Use for "can manage this user" checks.
 * @param {Object} user
 * @param {Object} otherUser
 * @returns {boolean}
 */
export const outranks = (user, otherUser) => getUserWeight(user) > getUserWeight(otherUser);

/**
 * Decides whether a role should be offered in a role selector.
 * Rules:
 *  - A user can assign roles ranked the same as or below their own highest role.
 *  - super-admin is never assignable from the UI, even by super admins.
 *  - A role the target user already holds is always shown, so the existing
 *    value still renders in the edit drawer.
 * Screen-specific exclusions (e.g. guest in the invite modal) stay at the call site.
 * @param {string} roleName - Role key being considered.
 * @param {Object} currentUser - The logged-in user.
 * @param {Object} [targetUser] - The user being edited. Omit when creating/inviting.
 * @returns {boolean}
 */
export const isRoleAssignable = (roleName, currentUser, targetUser) => {
  if (hasRole(targetUser, roleName)) return true;
  if (roleName === SUPER_ADMIN_ROLE) return false;
  return getRoleWeight(roleName) <= getUserWeight(currentUser);
};

/**
 * Filters a role list down to the roles that should be offered in a role selector.
 * Accepts both ROLE_NAMES items (`{ key, label }`) and API role items (`{ id, name }`).
 * @param {Array<Object>} roles - Role options to filter.
 * @param {Object} currentUser - The logged-in user.
 * @param {Object} [options]
 * @param {Object} [options.targetUser] - The user being edited. Omit when creating/inviting.
 * @param {Array<string>} [options.exclude] - Role keys to always hide on this screen, e.g. [GUEST_ROLE].
 * @returns {Array<Object>}
 */
export const getAssignableRoles = (roles, currentUser, { targetUser, exclude = [] } = {}) =>
  (roles ?? []).filter((role) => {
    const roleName = role?.key ?? role?.name;
    return !exclude.includes(roleName) && isRoleAssignable(roleName, currentUser, targetUser);
  });
